import React, { useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { Checkbox } from 'primereact/checkbox';
import { InputTextarea } from 'primereact/inputtextarea';

import { FormSectionElements } from '../../entities/formTemplateDataType';

type Props = {
    field: FormSectionElements;
    isEng: boolean;
    callback: (visible?: any) => void;
};

const fieldTypes = [
    { label: 'Text', value: 'Text' },
    { label: 'Number', value: 'Number' },
    { label: 'Date', value: 'Date' },
    { label: 'Dropdown', value: 'Dropdown' },
    { label: 'Multiple Choice', value: 'MultipleChoice' },
    { label: 'Read Only', value: 'ReadOnly' }
];

const DynamicAccordionFormDesignComponent = ({ field, isEng, callback }: Props) => {
    const defaultConfig = {
        titleField: '',
        entriesField: '',
        notDisplayHeader: false,
        notDisplayTitle: false,
        recordFilteringScript: '',
        formFields: []
    };

    const config = field.DynamicAccordionFormConfig || defaultConfig;
    const [localConfig, setLocalConfig] = useState(config);

    const updateConfig = (updates: Partial<typeof config>) => {
        const newConfig = { ...localConfig, ...updates };
        setLocalConfig(newConfig);
        field.DynamicAccordionFormConfig = newConfig;
        callback();
    };

    const updateFormField = (index: number, updates: Partial<typeof config.formFields[0]>) => {
        const newFields = [...localConfig.formFields];
        newFields[index] = { ...newFields[index], ...updates };
        updateConfig({ formFields: newFields });
    };

    const addDropdownOption = (fieldIndex: number) => {
        const newFields = [...localConfig.formFields];
        if (!newFields[fieldIndex].options) {
            newFields[fieldIndex].options = [];
        }
        newFields[fieldIndex].options.push({
            value: '',
            label: ''
        });
        updateConfig({ formFields: newFields });
    };

    const updateDropdownOption = (fieldIndex: number, optionIndex: number, updates: { value?: string; label?: string }) => {
        const newFields = [...localConfig.formFields];
        if (!newFields[fieldIndex].options) {
            newFields[fieldIndex].options = [];
        }
        newFields[fieldIndex].options[optionIndex] = { 
            ...newFields[fieldIndex].options[optionIndex], 
            ...updates 
        };
        updateConfig({ formFields: newFields });
    };

    const removeDropdownOption = (fieldIndex: number, optionIndex: number) => {
        const newFields = [...localConfig.formFields];
        if (newFields[fieldIndex].options) {
            newFields[fieldIndex].options.splice(optionIndex, 1);
            updateConfig({ formFields: newFields });
        }
    };

    const addFormField = () => {
        const newField = {
            bindName: '',
            label: '',
            type: 'Text' as const,
            required: false,
            editable: true
        };
        updateConfig({ formFields: [...localConfig.formFields, newField] });
    };

    const removeFormField = (index: number) => {
        const newFields = localConfig.formFields.filter((_, i) => i !== index);
        updateConfig({ formFields: newFields });
    };

    const moveFormField = (index: number, direction: -1 | 1) => {
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= localConfig.formFields.length) return;
        const newFields = [...localConfig.formFields];
        [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];
        updateConfig({ formFields: newFields });
    };

    return (
        <div className="dynamic-accordion-form-design" style={{ padding: '1rem' }}>
            <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    API Endpoint
                </label>
                <InputText
                    value={field.LovAPI || ''}
                    onChange={(e) => {
                        field.LovAPI = e.target.value;
                        callback();
                    }}
                    style={{ width: '100%' }}
                />
            </div>

                <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                        Data Path (optional)
                    </label>
                    <InputText
                        value={field.DataPath || ''}
                        onChange={(e) => {
                            field.DataPath = e.target.value;
                            callback();
                        }}
                        style={{ width: '100%' }}
                    />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                        Title Field
                    </label>
                    <InputText
                        value={localConfig.titleField}
                        onChange={(e) => updateConfig({ titleField: e.target.value })}
                        style={{ width: '100%' }}
                    />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                        Entries Array Field
                    </label>
                    <InputText
                        value={localConfig.entriesField}
                        onChange={(e) => updateConfig({ entriesField: e.target.value })}
                        style={{ width: '100%' }}
                    />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                        Not display header
                    </label>
                    <Checkbox
                        inputId={`notDisplayHeader`}
                        checked={localConfig.notDisplayHeader || false}
                        onChange={(e) => updateConfig({ notDisplayHeader: e.checked })}
                    />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                        Not display title
                    </label>
                    <Checkbox
                        inputId={`notDisplayTitle`}
                        checked={localConfig.notDisplayTitle || false}
                        onChange={(e) => updateConfig({ notDisplayTitle: e.checked })}
                    />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                        Record Filtering Script
                    </label>
                    <InputTextarea
                        className='ontario-input'
                        style={{ height: '120px', width: '100%' }}
                        value={localConfig.recordFilteringScript || ''}
                        onChange={(e) => updateConfig({ recordFilteringScript: e.target.value })}
                    />
                </div>

                {/* Pagination Settings */}
                <div style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
                    <label style={{ display: 'block', marginBottom: '1rem', fontWeight: 'bold' }}>
                        Pagination
                    </label>

                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                            Page Size <span style={{ color: '#666', fontWeight: 'normal' }}>(leave blank to disable pagination)</span>
                        </label>
                        <InputText
                            type="number"
                            value={field.PageSize != null ? String(field.PageSize) : ''}
                            onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                field.PageSize = isNaN(val) || val <= 0 ? undefined : val;
                                callback();
                            }}
                            style={{ width: '160px' }}
                        />
                    </div>

                    <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Checkbox
                            inputId="clientSidePagination"
                            checked={field.ClientSidePagination === true}
                            onChange={(e) => {
                                field.ClientSidePagination = e.checked;
                                callback();
                            }}
                        />
                        <label htmlFor="clientSidePagination" style={{ cursor: 'pointer' }}>
                            Client Side Pagination <span style={{ color: '#666', fontWeight: 'normal' }}>(fetch all data, paginate locally)</span>
                        </label>
                    </div>

                    {field.ClientSidePagination === true && (
                        <div style={{ marginBottom: '1rem', marginLeft: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Checkbox
                                inputId="refreshOnLoad"
                                checked={field.RefreshOnLoad === true}
                                onChange={(e) => {
                                    field.RefreshOnLoad = e.checked;
                                    callback();
                                }}
                            />
                            <label htmlFor="refreshOnLoad" style={{ cursor: 'pointer' }}>
                                Refresh on Load <span style={{ color: '#666', fontWeight: 'normal' }}>(re-fetch data on every visit instead of using cache)</span>
                            </label>
                        </div>
                    )}

                    {field.ClientSidePagination !== true && (
                        <>
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                    Total Count Path <span style={{ color: '#666', fontWeight: 'normal' }}>(e.g. totalCount, page.totalElements)</span>
                                </label>
                                <InputText
                                    value={field.TotalCountPath || ''}
                                    onChange={(e) => {
                                        field.TotalCountPath = e.target.value;
                                        callback();
                                    }}
                                    style={{ width: '100%' }}
                                />
                            </div>

                            <div style={{ marginBottom: '0.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                    Is Last Page Path <span style={{ color: '#666', fontWeight: 'normal' }}>(optional — for APIs that return a boolean instead of total count)</span>
                                </label>
                                <InputText
                                    value={field.IsLastPath || ''}
                                    onChange={(e) => {
                                        field.IsLastPath = e.target.value;
                                        callback();
                                    }}
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </>
                    )}
                </div>

                <div style={{ marginTop: '2rem', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <label style={{ fontWeight: 'bold' }}>
                            Form Fields
                        </label>
                        {/* @ts-ignore */}
                        <Button
                            label="Add Field"
                            icon="pi pi-plus"
                            onClick={addFormField}
                            size="small"
                        />
                    </div>

                    {localConfig.formFields.map((formField, index) => (
                        // @ts-ignore
                        <Panel
                            key={index}
                            header={`Field ${index + 1}${formField.bindName ? ` — ${formField.bindName}` : ''}${formField.type ? ` (${formField.type})` : ''}`}
                            style={{ marginBottom: '1rem' }}
                        >
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                        Bind Name
                                    </label>
                                    <InputText
                                        value={formField.bindName}
                                        onChange={(e) => updateFormField(index, { bindName: e.target.value })}
                                        style={{ width: '100%' }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                        Field Type
                                    </label>
                                    {/* @ts-ignore */}
                                    <Dropdown
                                        value={formField.type}
                                        options={fieldTypes}
                                        onChange={(e) => updateFormField(index, { type: e.value })}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                        Label (EN)
                                    </label>
                                    <InputText
                                        value={formField.label}
                                        onChange={(e) => updateFormField(index, { label: e.target.value })}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                        Label (FR)
                                    </label>
                                    <InputText
                                        value={(formField as any).labelFRA || ''}
                                        onChange={(e) => updateFormField(index, { labelFRA: e.target.value } as any)}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <Checkbox
                                        inputId={`required-${index}`}
                                        checked={formField.required || false}
                                        onChange={(e) => updateFormField(index, { required: e.checked })}
                                    />
                                    <label htmlFor={`required-${index}`} style={{ cursor: 'pointer' }}>
                                        Required
                                    </label>
                                </div>

                                {formField.type !== 'ReadOnly' && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <Checkbox
                                            inputId={`editable-${index}`}
                                            checked={formField.editable !== false}
                                            onChange={(e) => updateFormField(index, { editable: e.checked })}
                                        />
                                        <label htmlFor={`editable-${index}`} style={{ cursor: 'pointer' }}>
                                            Editable
                                        </label>
                                    </div>
                                )}
                            </div>

                            {/* Text / Number options */}
                            {(formField.type === 'Text' || formField.type === 'Number') && (
                                <div style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
                                    <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 'bold' }}>
                                        {formField.type} Options
                                    </label>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                                Placeholder
                                            </label>
                                            <InputText
                                                value={(formField as any).placeholder || ''}
                                                onChange={(e) => updateFormField(index, { placeholder: e.target.value } as any)}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                                Max Length
                                            </label>
                                            <InputText
                                                type="number"
                                                value={(formField as any).maxLength?.toString() || ''}
                                                onChange={(e) => {
                                                    const val = parseInt(e.target.value, 10);
                                                    updateFormField(index, { maxLength: isNaN(val) || val <= 0 ? undefined : val } as any);
                                                }}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Dropdown Options Configuration */}
                            {(formField.type === 'Dropdown' || formField.type === 'MultipleChoice') && (
                                <div style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                        <label style={{ fontWeight: 'bold' }}>
                                            {formField.type === 'Dropdown' ? 'Dropdown Options' : 'Multiple Choice Options'}
                                        </label>
                                        {/* @ts-ignore */}
                                        <Button
                                            label="Add Option"
                                            icon="pi pi-plus"
                                            onClick={() => addDropdownOption(index)}
                                            size="small"
                                        />
                                    </div>

                                    {formField.options && formField.options.length > 0 ? (
                                        formField.options.map((option, optionIndex) => (
                                            <div key={optionIndex} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                                                <div style={{ flex: '1' }}>
                                                    <InputText
                                                        placeholder="Value"
                                                        value={option.value || ''}
                                                        onChange={(e) => updateDropdownOption(index, optionIndex, { value: e.target.value })}
                                                        style={{ width: '100%' }}
                                                    />
                                                </div>
                                                <div style={{ flex: '1' }}>
                                                    <InputText
                                                        placeholder="Label"
                                                        value={option.label || ''}
                                                        onChange={(e) => updateDropdownOption(index, optionIndex, { label: e.target.value })}
                                                        style={{ width: '100%' }}
                                                    />
                                                </div>
                                                {/* @ts-ignore */}
                                                <Button
                                                    icon="pi pi-trash"
                                                    onClick={() => removeDropdownOption(index, optionIndex)}
                                                    severity="danger"
                                                    size="small"
                                                    text
                                                />
                                            </div>
                                        ))
                                    ) : (
                                        <p style={{ color: '#666', fontStyle: 'italic', margin: '0.5rem 0' }}>
                                            No options configured. Click "Add Option" to add {formField.type === 'Dropdown' ? 'dropdown choices' : 'multiple choice options'}.
                                        </p>
                                    )}
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                {/* @ts-ignore */}
                                <Button
                                    label="Remove Field"
                                    icon="pi pi-trash"
                                    onClick={() => removeFormField(index)}
                                    severity="danger"
                                    size="small"
                                />
                                {localConfig.formFields.length > 1 && (
                                    <>
                                        {/* @ts-ignore */}
                                        <Button
                                            icon="pi pi-chevron-up"
                                            disabled={index === 0}
                                            onClick={() => moveFormField(index, -1)}
                                            title="Move field up"
                                            size="small"
                                            text
                                        />
                                        {/* @ts-ignore */}
                                        <Button
                                            icon="pi pi-chevron-down"
                                            disabled={index === localConfig.formFields.length - 1}
                                            onClick={() => moveFormField(index, 1)}
                                            title="Move field down"
                                            size="small"
                                            text
                                        />
                                    </>
                                )}
                            </div>
                        </Panel>
                    ))}
                </div>
        </div>
    );
};

export default DynamicAccordionFormDesignComponent;
